import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import sortBy from "lodash/sortBy";
import { Scrollbars } from "react-custom-scrollbars";
import { selectedFiltersContext } from ".";
import { FilterOption, FilterSizeVariants, GeneralFacet } from "../types";
import SizeFilterSearchHeader from "./components/SizeFilterSearchHeader";
import SizeOptionList from "./components/SizeOptionList";
import SizeAdvancedView from "./components/SizeAdvancedView";
import SizeFormatSelectMob from "./components/SizeFormatSelectMob";
import { useSizeFilterHelpers } from "../hooks/useSizeFilterHelpers";
import { useSizeFilterMode } from "../hooks/useSizeFilterMode";
import { useSizeFilterAutoInit } from "../hooks/useSizeFilterAutoInit";
import { getFacetIdentity } from "../utils";

const FilterSizeContainerMob: FC<{
  filter: GeneralFacet;
  simpleGroups: any[];
  advancedGroups: any[];
}> = ({ filter, simpleGroups, advancedGroups }) => {
  const { t } = useTranslation("productListing");
  const {
    getNodeKey,
    flattenLeafOptions,
    getUniqueOptionsByKey,
    filterOptionsWithValidLabel,
    getOptionSizeFormat
  } = useSizeFilterHelpers();

  const { updateSelectedFilters, selectedFilters, onSelection } = useContext(
    selectedFiltersContext
  );

  const queryParam = filter.queryParam as string;

  const facetId = useMemo(
    () => getFacetIdentity(filter),
    [
      filter.type,
      filter.queryParam,
      filter.parentCategoryKey,
      filter.label,
      filter.defaultLabel
    ]
  );

  const allOptionKeysInFacet = useMemo(() => {
    const keys = new Set<string>();
    simpleGroups.forEach(group => {
      flattenLeafOptions(group).forEach(option => {
        if (typeof option?.key === "string") keys.add(option.key);
      });
    });
    advancedGroups.forEach(group => {
      flattenLeafOptions(group).forEach(option => {
        if (typeof option?.key === "string") keys.add(option.key);
      });
    });
    return keys;
  }, [simpleGroups, advancedGroups, flattenLeafOptions]);

  const filterOptionsToCurrentFacet = useCallback(
    (options: FilterOption[] | undefined | null): FilterOption[] => {
      if (!Array.isArray(options)) return [];
      if (allOptionKeysInFacet.size === 0) return options;
      return options.filter(
        (option): option is FilterOption =>
          !!option &&
          typeof option.key === "string" &&
          allOptionKeysInFacet.has(option.key)
      );
    },
    [allOptionKeysInFacet]
  );

  const mergeWithGlobalSelections = useCallback(
    (
      globalOptions: FilterOption[] | undefined | null,
      facetScopedOptions: FilterOption[]
    ): FilterOption[] => {
      const otherFacetOptions = (globalOptions || []).filter(
        option => option?.key && !allOptionKeysInFacet.has(option.key)
      );
      return [...otherFacetOptions, ...facetScopedOptions];
    },
    [allOptionKeysInFacet]
  );

  const modeSwitchClearedSelectionsRef = useRef(false);

  const {
    activeMode,
    activeModeOrigin,
    setMode,
    userChosenModeByFacetId,
    setUserChosenModeByFacetId
  } = useSizeFilterMode({
    facetId,
    queryParam,
    selectedFilters,
    simpleGroups,
    advancedGroups,
    flattenLeafOptions,
    filterOptionsToCurrentFacet,
    modeSwitchClearedSelectionsRef
  });

  const {
    selectedOptions,
    setSelectedOptions,
    selectedSizeKeys,
    expandedAccordionKey,
    setExpandedAccordionKey,
    expandedGroupNode,
    activeSizeFormatByGroup,
    setActiveSizeFormatByGroup,
    activeTabByGroup,
    setActiveTabByGroup,
    activeSubgroupByGroup,
    setActiveSubgroupByGroup,
    userInteractedWithSizesRef,
    toggleAccordionGroup,
    updateAdvancedLeafOptions,
    setUserToggledAccordionManually
  } = useSizeFilterAutoInit({
    facetId,
    mode: activeMode,
    modeOrigin: activeModeOrigin,
    setMode,
    simpleGroups,
    advancedGroups,
    selectedFilters,
    filter,
    filterOptionsToCurrentFacet
  });

  const [searchQuery, setSearchQuery] = React.useState("");

  useEffect(() => {
    setSearchQuery("");
  }, [facetId]);

  const searchQueryLowerCase = useMemo(
    () => searchQuery.toLocaleLowerCase(),
    [searchQuery]
  );
  const isSearchActive = searchQueryLowerCase.length > 0;

  const groupsForActiveMode = useMemo(
    () =>
      activeMode === FilterSizeVariants.SIMPLE ? simpleGroups : advancedGroups,
    [simpleGroups, advancedGroups, activeMode]
  );

  const groupsMatchingSearch = useMemo(() => {
    if (!searchQueryLowerCase) return groupsForActiveMode;
    return groupsForActiveMode.filter(group => {
      const groupOptions = flattenLeafOptions(group);
      return groupOptions.some(option =>
        option.label?.toLocaleLowerCase().includes(searchQueryLowerCase)
      );
    });
  }, [groupsForActiveMode, searchQueryLowerCase, flattenLeafOptions]);

  const sortedOpts = (options: FilterOption[]) =>
    sortBy(filterOptionsWithValidLabel(getUniqueOptionsByKey(options)), [
      "sortOrder"
    ]);

  const renderGroupContent = (
    node: any,
    isCountVisible: boolean
  ): React.ReactNode => {
    const nodeKey = getNodeKey(node) || "";
    const children: any[] = node.children || [];
    const layout: string | undefined = node.childLayout;

    if (layout === "dropdown") {
      const childNodes = children.filter((c: any) => c.children?.length > 0);
      if (childNodes.length === 0) {
        return (
          <SizeOptionList
            options={sortedOpts(flattenLeafOptions(node))}
            selectedKeys={selectedSizeKeys}
            onOptionToggle={handleSizeOptionToggle}
            isCountVisible={isCountVisible}
          />
        );
      }
      const getChildFormatOrKey = (c: any): string =>
        getOptionSizeFormat(c) || c.key || "";
      const storedKey = activeSizeFormatByGroup[nodeKey];
      const selectedChild = storedKey
        ? childNodes.find(
            (c: any) =>
              getChildFormatOrKey(c) === storedKey || c.key === storedKey
          ) || childNodes[0]
        : childNodes[0];
      const selectedChildKey = selectedChild?.key || childNodes[0]?.key || "";
      return (
        <>
          <SizeFormatSelectMob
            options={childNodes.map((c: any) => ({
              key: c.key,
              label: c.label || c.defaultValue || ""
            }))}
            value={selectedChildKey}
            onChange={(value: string) => {
              const picked = childNodes.find((c: any) => c.key === value);
              const formatOrKey = picked ? getChildFormatOrKey(picked) : value;
              setActiveSizeFormatByGroup(prev => ({
                ...prev,
                [nodeKey]: formatOrKey
              }));
            }}
            title={t("size")}
          />
          {selectedChild && renderGroupContent(selectedChild, isCountVisible)}
        </>
      );
    }

    if (layout === "tabs") {
      const selectedTabKey =
        activeTabByGroup[nodeKey] || children[0]?.key || "";
      const selectedTab =
        children.find((c: any) => c.key === selectedTabKey) || children[0];
      return (
        <>
          <div className="size_subnav_row">
            {children.map((child: any) => (
              <button
                key={child.key}
                className={cn("size_tab_button", {
                  active: child.key === selectedTabKey
                })}
                onClick={() =>
                  setActiveTabByGroup(prev => ({
                    ...prev,
                    [nodeKey]: child.key
                  }))
                }
              >
                {child.label || child.defaultValue}
              </button>
            ))}
          </div>
          {selectedTab && renderGroupContent(selectedTab, isCountVisible)}
        </>
      );
    }

    if (layout === "sections") {
      const sectionChildren = children.filter(
        (c: any) => c.children?.length > 0
      );
      const leafChildren = children.filter(
        (c: any) => !c.children?.length
      ) as FilterOption[];
      return (
        <>
          {leafChildren.length > 0 && (
            <SizeOptionList
              options={sortedOpts(leafChildren)}
              selectedKeys={selectedSizeKeys}
              onOptionToggle={handleSizeOptionToggle}
              isCountVisible={isCountVisible}
            />
          )}
          {sectionChildren.map((section: any, si: number) => {
            const sectionLabel = section.label || section.defaultValue;
            return (
              <div
                key={section.key || `section-${si}`}
                className="size_section_mob"
              >
                {sectionLabel && (
                  <div className="size_section_label_mob">{sectionLabel}</div>
                )}
                <SizeOptionList
                  options={sortedOpts(flattenLeafOptions(section))}
                  selectedKeys={selectedSizeKeys}
                  onOptionToggle={handleSizeOptionToggle}
                  isCountVisible={isCountVisible}
                />
              </div>
            );
          })}
        </>
      );
    }

    return (
      <SizeOptionList
        options={sortedOpts(flattenLeafOptions(node))}
        selectedKeys={selectedSizeKeys}
        onOptionToggle={handleSizeOptionToggle}
        isCountVisible={isCountVisible}
      />
    );
  };

  const handleSizeOptionToggle = useCallback(
    (option: FilterOption, event: React.MouseEvent) => {
      event.stopPropagation();
      userInteractedWithSizesRef.current = true;

      const isAlreadySelected = selectedOptions.some(
        item => item.key === option.key
      );
      const updatedOptions = isAlreadySelected
        ? selectedOptions.filter(item => item.key !== option.key)
        : [...selectedOptions, option];

      if (
        updatedOptions.length === 0 &&
        !(facetId in userChosenModeByFacetId)
      ) {
        setUserChosenModeByFacetId(prev => ({
          ...prev,
          [facetId]: activeMode
        }));
      }

      const globalOptions =
        selectedFilters?.[queryParam]?.selectedOptions ?? [];
      const mergedOptions = mergeWithGlobalSelections(
        globalOptions,
        updatedOptions
      );

      setSelectedOptions(updatedOptions);
      updateSelectedFilters({
        ...filter,
        selectedOptions: mergedOptions
      });
    },
    [
      selectedOptions,
      filter,
      updateSelectedFilters,
      selectedFilters,
      queryParam,
      mergeWithGlobalSelections,
      facetId,
      userChosenModeByFacetId,
      setUserChosenModeByFacetId,
      activeMode,
      setSelectedOptions,
      userInteractedWithSizesRef
    ]
  );

  const handleSimpleAdvancedModeSwitch = useCallback(() => {
    const previousMode = activeMode;
    const nextMode =
      previousMode === FilterSizeVariants.SIMPLE
        ? FilterSizeVariants.ADVANCED
        : FilterSizeVariants.SIMPLE;

    const previousModeGroups =
      previousMode === FilterSizeVariants.SIMPLE
        ? simpleGroups
        : advancedGroups;
    const keysToRemoveFromPreviousMode = new Set(
      previousModeGroups
        .flatMap(group => flattenLeafOptions(group))
        .map(option => option.key)
    );

    const hadSelectionsInPreviousMode = selectedOptions.some(
      option => option?.key && keysToRemoveFromPreviousMode.has(option.key)
    );

    if (hadSelectionsInPreviousMode) {
      modeSwitchClearedSelectionsRef.current = true;

      const optionsWithoutPreviousMode = selectedOptions.filter(
        option => !keysToRemoveFromPreviousMode.has(option.key)
      );
      const globalOptions =
        selectedFilters?.[queryParam]?.selectedOptions ?? [];
      const mergedOptions = mergeWithGlobalSelections(
        globalOptions,
        optionsWithoutPreviousMode
      );

      setSelectedOptions(optionsWithoutPreviousMode);
      onSelection({ ...filter, selectedOptions: mergedOptions });
    }
    setSearchQuery("");
    setMode(nextMode, "user");
    setExpandedAccordionKey(null);
    setUserToggledAccordionManually(false);
  }, [
    activeMode,
    setMode,
    simpleGroups,
    advancedGroups,
    flattenLeafOptions,
    selectedOptions,
    selectedFilters,
    queryParam,
    mergeWithGlobalSelections,
    filter,
    onSelection,
    setSelectedOptions,
    setExpandedAccordionKey,
    setUserToggledAccordionManually
  ]);

  return (
    <div className="filter_content" onClick={event => event.stopPropagation()}>
      <SizeFilterSearchHeader
        searchValue={searchQuery}
        onSearchChange={event =>
          setSearchQuery(event.target.value.toLocaleLowerCase())
        }
        onSearchClear={() => setSearchQuery("")}
        placeholder={filter.search?.placeholder}
        label={filter.label}
      />

      <div className="filter_content_scroll_host">
        <div className="filter_content_scroll_area">
          <Scrollbars style={{ width: "100%", height: "100%" }}>
            <div className="size_simple_groups">
              {groupsMatchingSearch.map(group => {
                const groupKey = getNodeKey(group) || "";
                const isGroupExpanded = isSearchActive
                  ? true
                  : groupKey === expandedAccordionKey;
                const isGroupCountVisible = !!group.isCountVisible;

                const searchMatchedOptions = isSearchActive
                  ? sortedOpts(
                      flattenLeafOptions(group).filter(option =>
                        option.label
                          ?.toLocaleLowerCase()
                          .includes(searchQueryLowerCase)
                      )
                    )
                  : [];

                const groupLeafKeys = new Set(
                  flattenLeafOptions(group).map(o => o.key)
                );
                const groupSelectedCount = selectedSizeKeys.filter(k =>
                  groupLeafKeys.has(k)
                ).length;

                return (
                  <div className="size_simple_group" key={groupKey}>
                    <div
                      className={cn("size_simple_group_header", {
                        open: isGroupExpanded
                      })}
                      onClick={() => toggleAccordionGroup(groupKey)}
                    >
                      <span className="size_simple_group_title">
                        {group.label || group.defaultValue}
                      </span>
                      {groupSelectedCount > 0 && (
                        <span className="selected_filter_count">
                          {groupSelectedCount}
                        </span>
                      )}
                      <span className="size_group_arrow" />
                    </div>

                    {isGroupExpanded && (
                      <div className="size_simple_group_body">
                        {isSearchActive ? (
                          <SizeOptionList
                            options={searchMatchedOptions}
                            selectedKeys={selectedSizeKeys}
                            onOptionToggle={handleSizeOptionToggle}
                            isCountVisible={isGroupCountVisible}
                          />
                        ) : activeMode === FilterSizeVariants.SIMPLE ? (
                          renderGroupContent(group, isGroupCountVisible)
                        ) : (
                          <SizeAdvancedView
                            group={expandedGroupNode}
                            searchQuery={searchQueryLowerCase}
                            selectedKeys={selectedSizeKeys}
                            onOptionToggle={handleSizeOptionToggle}
                            activeTabKey={activeTabByGroup[groupKey] || null}
                            onTabChange={key =>
                              setActiveTabByGroup(prev => ({
                                ...prev,
                                [groupKey]: key
                              }))
                            }
                            activeSubgroupKey={
                              activeSubgroupByGroup[groupKey] || null
                            }
                            onSubgroupChange={key =>
                              setActiveSubgroupByGroup(prev => ({
                                ...prev,
                                [groupKey]: key
                              }))
                            }
                            selectTitle={t("size")}
                            onLeafOptionsChange={options =>
                              updateAdvancedLeafOptions(groupKey, options)
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Scrollbars>
        </div>
      </div>
    </div>
  );
};

export default FilterSizeContainerMob;
